// Cell values are text — except that some AMDPs return a little markup (a <br>, a bold word,
// a coloured span), which the old renderer inserted as raw HTML. This keeps that formatting
// and drops everything that could run script or load anything.

const ALLOWED = new Set(['B', 'STRONG', 'I', 'EM', 'U', 'S', 'BR', 'SPAN', 'SMALL', 'SUB', 'SUP', 'FONT', 'DIV', 'P', 'MARK']);
const STYLE_PROPS = new Set(['color', 'background-color', 'background', 'font-weight', 'font-size', 'font-style', 'text-decoration']);

const looksLikeHtml = /<\s*\/?\s*[a-z][^>]*>/i;

export function needsHtml(value: string): boolean {
  return value.indexOf('<') >= 0 && looksLikeHtml.test(value);
}

let template: HTMLTemplateElement | null = null;

export function sanitizeHtml(html: string): string {
  template ||= document.createElement('template');
  template.innerHTML = html;
  clean(template.content);
  return template.innerHTML;
}

function clean(node: Node): void {
  for (const child of Array.from(node.childNodes)) {
    if (child.nodeType === Node.TEXT_NODE) continue;
    if (child.nodeType !== Node.ELEMENT_NODE) {
      child.remove();
      continue;
    }

    const el = child as HTMLElement;
    if (!ALLOWED.has(el.tagName)) {
      // Unknown element: keep its text, lose the element.
      if (el.tagName === 'SCRIPT' || el.tagName === 'STYLE' || el.tagName === 'IFRAME' || el.tagName === 'OBJECT') {
        el.remove();
      } else {
        clean(el);
        el.replaceWith(...Array.from(el.childNodes));
      }
      continue;
    }

    for (const attr of Array.from(el.attributes)) {
      const name = attr.name.toLowerCase();
      if (name === 'style') {
        el.setAttribute('style', safeStyle(attr.value));
      } else if (name === 'color' && el.tagName === 'FONT') {
        if (!/^[#a-z0-9(),.\s%-]+$/i.test(attr.value)) el.removeAttribute(name);
      } else {
        el.removeAttribute(attr.name);
      }
    }
    clean(el);
  }
}

function safeStyle(style: string): string {
  return style
    .split(';')
    .map((decl) => {
      const i = decl.indexOf(':');
      if (i < 0) return '';
      const prop = decl.slice(0, i).trim().toLowerCase();
      const value = decl.slice(i + 1).trim();
      if (!STYLE_PROPS.has(prop)) return '';
      if (/url\s*\(|expression|javascript:/i.test(value)) return '';
      return prop + ':' + value;
    })
    .filter(Boolean)
    .join(';');
}
